import { GraphQLTypes } from "./zeus";

type TFragment<T = unknown> = {
  [key in keyof T]: true;
};

type TQuestionFragment = TFragment<GraphQLTypes["QuestionType"]>;
type TQuestionSetFragment = TFragment<GraphQLTypes["QuestionSetType"]>;
type TEnquiryFragment = TFragment<GraphQLTypes["EnquiryType"]>;
type TAnswerFragment = TFragment<GraphQLTypes["AnswerType"]>;
type TDataProviderFragment = TFragment<GraphQLTypes["DataProviderType"]>;
type TRhetoricalFragment = TFragment<GraphQLTypes["RhetoricalType"]>;
type TRiskProfileFragment = TFragment<GraphQLTypes["RiskProfileType"]>;
type TClaimProfileFragment = TFragment<GraphQLTypes["ClaimProfileType"]>;
type TQuoteFragment = TFragment<GraphQLTypes["QuoteType"]>;
type TPolicyFragment = TFragment<GraphQLTypes["PolicyType"]>;
type TUserFragment = TFragment<GraphQLTypes["UserType"]>;
type TCoversFragment = TFragment<GraphQLTypes["CoverType"]>;
type TExposureFragment = TFragment<GraphQLTypes["ExposureType"]>;
type TExposureQuestionSetFragment = TFragment<GraphQLTypes["ExposureQuestionSetType"]>;

const rhetoricalFields: TRhetoricalFragment = {
  id: true,
  kind: true,
  args: true,
  order: true,
};

const questionFields: TQuestionFragment = {
  id: true,
  help: true,
  label: true,
  title: true,
  inputType: true,
  inputArgs: true,
  inputForm: {
    id: true,
    rhetoricals: rhetoricalFields,
    questions: {
      id: true,
      help: true,
      label: true,
      inputType: true,
      inputArgs: true,
      storagePath: true,
      order: true,
      validators: true,
      default: true,
      rhetoricals: rhetoricalFields,
    },
  },
  storagePath: true,
  order: true,
  validators: true,
  default: true,
  rhetoricals: rhetoricalFields,
  metadata: true,
};

const questionSetFields: TQuestionSetFragment = {
  id: true,
  key: true,
  title: true,
  description: true,
  order: true,
  help: true,
  validators: true,
  rhetoricals: rhetoricalFields,
  questions: {
    ...questionFields,
  },
};

const riskStateQuestionSetFields: TQuestionSetFragment = {
  id: true,
  title: true,
  description: true,
  key: true,
  questions: {
    id: true,
    help: true,
    label: true,
    displayLabel: true,
    inputType: true,
    inputArgs: true,
    storagePath: true,
    order: true,
    validators: true,
    default: true,
  },
  order: true,
};

const enquiryFields: TEnquiryFragment = {
  id: true,
  questionFlow: {
    id: true,
  },
};

const answerFields: TAnswerFragment = {
  id: true,
  value: true,
  source: true,
};

const dataProviderFields: TDataProviderFragment = {
  id: true,
  name: true,
};

const claimProfileFields: TClaimProfileFragment = {
  id: true,
  data: true,
};
const coverFields: TCoversFragment = {
  id: true,
  key: true,
  title: true,
  description: true,
  icon: true,
  editable: true,
  product: {
    provider: {
      key: true,
      name: true,
      logo: true,
    },
    key: true,
    name: true,
  },
  subCovers: {
    id: true,
    key: true,
  },
  parentCover: {
    id: true,
    key: true,
    icon: true,
  },
  coverQuestionSets: {
    questionSet: {
      id: true,
      key: true,
      order: true,
    },
  },
};
const exposureQuestionSetFields: TExposureQuestionSetFragment = {
  questionSet: {
    id: true,
    title: true,
    description: true,
    key: true,
    questions: {
      id: true,
      help: true,
      label: true,
      inputType: true,
      inputArgs: true,
      storagePath: true,
      order: true,
      validators: true,
      default: true,
    },
    order: true,
  },
  exposureItemKey: true,
};
const exposureFields: TExposureFragment = {
  id: true,
  key: true,
  type: true,
  name: true,
  covers: {
    ...coverFields,
  },
  exposureQuestionSets: {
    ...exposureQuestionSetFields,
  },
};

const quoteFields: TQuoteFragment = {
  id: true,
  periodicPrice: true,
  paymentFrequency: true,
  userReference: true,
  preTaxPremium: true,
  postTaxPremium: true,
  commission: true,
  pricingOthers: true,
  installments: {
    kind: true,
    inclusivePrice: true,
  },
  messages: true,
  state: true,
  product: {
    key: true,
    name: true,
    autoRenewAllowed: true,
    paymentGateway: true,
    skipPayments: true,
    quoteDisclaimer: true,
    exposures: {
      ...exposureFields,
    },
  },
  covers: {
    ...coverFields,
  },
  documents: {
    name: true,
    url: true,
  },
  limitations: {
    cover: {
      title: true,
      description: true,
      key: true,
    },
    key: true,
    kind: true,
    text: true,
    description: true,
  },
  fees: {
    taxes: true,
    fees: true,
    stampDuty: true,
    breakdown: {
      amount: true,
      feeType: {
        key: true,
        label: true,
        kind: true,
      },
    },
  },
};

const riskProfileFields: TRiskProfileFragment = {
  id: true,
  riskData: true,
  data: true,
  periodicPrice: true,
  paymentFrequency: true,
  state: true,
  availableProducts: {
    key: true,
  },
  enabledProducts: {
    key: true,
  },
  referredProducts: {
    key: true,
    name: true,
  },
  knockoutProducts: {
    key: true,
    name: true,
  },
  enabledCovers: {
    key: true,
    title: true,
    product: {
      key: true,
    },
  },
  enabledExposures: true,
  quotes: {
    ...quoteFields,
  },
};

const policyFields: TPolicyFragment = {
  id: true,
  status: true,
  statusDisplay: true,
  riskProfile: riskProfileFields,
  startDate: true,
  finishDate: true,
  policyNumber: true,
  autoRenewEnabled: true,
  isWithinRenewalPeriod: true,
  isPendingCancellation: true,
  updateAllowed: true,
  cancellationAllowed: true,
  claimsAllowed: true,
  draftUpdate: {
    ...quoteFields,
    riskProfile: riskProfileFields,
  },
  quote: {
    ...quoteFields,
    riskProfile: {
      riskData: true,
      id: true,
    },
    product: {
      ...quoteFields.product,
    },
  },
  latestRenewal: {
    id: true,
    canDiscard: true,
    canAccept: true,
    quote: {
      id: true,
      riskProfile: riskProfileFields,
      stateDisplay: true,
      state: true,
      createdTimestamp: true,
      amountDue: true,
      startDate: true,
      isBound: true,
      product: {
        provider: {
          name: true,
        },
      },
      documents: {
        type: true,
        url: true,
        name: true,
      },
      paymentFrequency: true,
    },
  },
  latestUpdate: {
    quote: {
      riskProfile: riskProfileFields,
      state: true,
      referralState: true,
    },
  },
  latestCancellation: {
    amountDue: true,
    fees: {
      taxes: true,
      fees: true,
      stampDuty: true,
      breakdown: {
        feeType: {
          key: true,
        },
        amount: true,
      },
    },
    effectiveDate: true,
    data: true,
  },
  documents: {
    type: true,
    url: true,
    name: true,
  },
};
const quoteFlowFields = {
  currentEnquiry: {
    ...enquiryFields,
    questionSet: {
      ...questionSetFields,
      questions: {
        ...questionFields,
      },
    },
  },
  history: {
    questionSet: {
      id: true,
      key: true,
      label: true,
    },
  },
  riskProfile: {
    ...riskProfileFields,
  },
  maxSets: true,
  currentSet: true,
};

const userFields: TUserFragment = {
  id: true,
  uuid: true,
  email: true,
  firstName: true,
  lastName: true,
  contactNumber: true,
  contactEmail: true,
  isLazy: true,
  isHijacked: true,
  isPasswordLess: true,
  isSsoEnabled: true,
  isStaff: true,
  allowCustomerPortalAccess: true,
  allowAdminPortalAccess: true,
  allowUpdatePaymentMethod: true,
  allowReportAccess: true,
  dateJoined: true,
  latestReferrer: {
    key: true,
    name: true,
  },
  organization: {
    name: true,
    key: true,
    operatingHours: true,
    phoneDisplay: true,
    emailAddress: true,
    website: true,
    disclaimer: true,
    disclaimerLinks: {
      name: true,
      url: true,
    },
  },
};

export {
  questionFields,
  questionSetFields,
  riskStateQuestionSetFields,
  exposureFields,
  enquiryFields,
  answerFields,
  coverFields,
  dataProviderFields,
  riskProfileFields,
  claimProfileFields,
  quoteFields,
  policyFields,
  quoteFlowFields,
  userFields,
};
